/**
 * Bugs — decide in one place. A failure that is obviously an application
 * error goes to Support on its own (backend `ui_testing/routing.py`); what
 * needs a decision — the test may be wrong, it keeps failing, the AI already
 * tried, Support declined it — waits here with the reasons. The reviewer
 * decides: fix it, saying what should change and how (that guidance travels
 * to every Support stage), or close it with a reason so the next run does not
 * raise it again. A Support issue can still be raised for any single bug at
 * any time — a closed one included.
 *
 * Backed by the defect register (`/api/ui-testing/{project}/defects`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { ChevronDown, ChevronRight, Plus } from 'lucide-react'
import Button from '@/components/common/Button'
import Modal from '@/components/common/Modal'
import {
  apiError, closeDefects, createDefect, decideDefects, listDefects, raiseIssue, reopenDefects,
  runFileUrl, updateDefect,
  type Defect, type DefectClassification, type DefectStatus, type DefectTarget, type LogResult, type Severity,
} from '@/api/uiTesting'
import type { TestingNav } from './TestingPage'
import { Badge, Lightbox, Thumb, fmtTime, inputStyle, mutedText, panel, type Tone } from './shared'
import { nice } from './plainSteps'

interface Props {
  project: string
  canTest: boolean
  nav: TestingNav
  onChanged: () => Promise<void> | void
}

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low']
const SEV_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }
const SEV_TONE: Record<string, Tone> = { critical: 'err', high: 'err', medium: 'warn', low: 'neutral' }
const CLASSES: { value: DefectClassification; label: string }[] = [
  { value: 'product', label: 'A bug in the application' },
  { value: 'test', label: 'The test is wrong' },
  { value: 'environment', label: 'The test environment' },
  { value: 'unclassified', label: 'Not decided' },
]
const CLOSE_REASONS = ['Not a bug', 'Won’t fix', 'Duplicate', 'The test is wrong']
const TARGETS: { value: DefectTarget; label: string; help: string }[] = [
  { value: 'application', label: 'The application', help: 'A real bug — Support changes the screen, rule or logic.' },
  { value: 'test', label: 'The test case', help: 'The application is right — Support updates what the test expects.' },
  { value: 'data', label: 'Test data or set-up', help: 'Seed data, a persona’s rights, or a setting the test relies on.' },
]
const MIN_GUIDANCE = 10
type Filter = DefectStatus | 'all'

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'open', label: 'Needs a decision' },
  { key: 'logged', label: 'In Support' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
]

export default function DefectsTab({ project, canTest, nav, onChanged }: Props) {
  const [filter, setFilter] = useState<Filter>('open')
  const [rows, setRows] = useState<Defect[]>([])
  const [counts, setCounts] = useState<Record<DefectStatus, number>>({ open: 0, logged: 0, closed: 0 })
  const [loading, setLoading] = useState(true)
  const [sel, setSel] = useState<Set<number>>(new Set())
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [busy, setBusy] = useState<string | null>(null)
  const [closing, setClosing] = useState<Defect[] | null>(null)
  const [deciding, setDeciding] = useState<Defect[] | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [light, setLight] = useState<{ src: string; caption?: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await listDefects(project, { status: filter === 'all' ? undefined : filter })
      setRows(r.defects)
      setCounts(r.counts)
      setSel((s) => new Set([...s].filter((id) => r.defects.some((d) => d.id === id))))
    } catch (err) {
      toast.error(apiError(err, 'Could not load the bugs'))
    } finally {
      setLoading(false)
    }
  }, [project, filter])

  useEffect(() => { void load() }, [load])

  const sorted = useMemo(() => [...rows].sort((a, b) =>
    (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9) || a.number - b.number), [rows])
  const refresh = async () => { await load(); await onChanged() }
  const selected = sorted.filter((d) => sel.has(d.id))
  const allChecked = sorted.length > 0 && sorted.every((d) => sel.has(d.id))
  const total = counts.open + counts.logged + counts.closed

  const reportLog = (r: LogResult) => {
    if (r.logged.length) toast.success(`Sent to Support: ${r.logged.map((x) => `${x.ref} → #${x.issue}`).join(', ')}`)
    if (r.skipped.length) toast(`Skipped: ${r.skipped.map((x) => `${x.ref} (${x.reason})`).join(', ')}`)
    if (r.failed.length) toast.error(`Support refused: ${r.failed.map((x) => `${x.ref}: ${x.error}`).join('; ')}`)
    if (!r.logged.length && !r.skipped.length && !r.failed.length) toast('There was nothing to send')
  }

  const act = async (key: string, fn: () => Promise<void>) => {
    setBusy(key)
    try { await fn() } finally { setBusy(null) }
  }

  const onDecide = (list: Defect[]) => {
    const open = list.filter((d) => d.status === 'open')
    if (open.length) setDeciding(open)
  }

  const onReopen = (list: Defect[], key: string) => act(key, async () => {
    try {
      const r = await reopenDefects(project, list.filter((d) => d.status === 'closed').map((d) => d.id))
      toast.success(`Reopened ${r.reopened.join(', ') || 'nothing'}`)
      setSel(new Set())
      await refresh()
    } catch (err) {
      toast.error(apiError(err, 'Could not reopen the bugs'))
    }
  })

  const onRaise = (d: Defect) => act(`raise-${d.id}`, async () => {
    try {
      const r = await raiseIssue(project, d.id)
      toast.success(`${r.ref} raised as Support issue #${r.issue}`)
      await refresh()
    } catch (err) {
      toast.error(apiError(err, `Could not raise an issue for ${d.ref}`))
    }
  })

  const onPatch = async (d: Defect, fields: Partial<Pick<Defect, 'severity' | 'classification'>>) => {
    try {
      const next = await updateDefect(project, d.id, fields)
      setRows((list) => list.map((x) => (x.id === d.id ? { ...x, ...next } : x)))
    } catch (err) {
      toast.error(apiError(err, `Could not update ${d.ref}`))
    }
  }

  const toggle = (set: Set<number>, id: number) => {
    const n = new Set(set)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  }

  const countOf = (f: Filter) => (f === 'all' ? total : counts[f])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div role="tablist" aria-label="Bug status" style={{
          ...panel, display: 'inline-flex', gap: 4, padding: 4, borderRadius: 10, flexWrap: 'wrap',
        }}>
          {FILTERS.map(({ key, label }) => {
            const on = filter === key
            const n = countOf(key)
            return (
              <button key={key} type="button" role="tab" aria-selected={on}
                      onClick={() => { setFilter(key); setSel(new Set()) }} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 36, padding: '0 14px',
                borderRadius: 8, fontSize: 15, fontWeight: 600,
                background: on ? 'var(--color-secondary-btn)' : 'transparent',
                color: on ? 'var(--color-text)' : 'var(--color-text-secondary)',
              }}>
                {label}
                <Badge tone={key === 'open' && n > 0 ? 'err' : 'neutral'} style={{ padding: '3px 7px', fontSize: 13 }}>{n}</Badge>
              </button>
            )
          })}
        </div>
        {canTest && (
          <Button variant="ghost" icon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
            Add a bug you found yourself
          </Button>
        )}
      </div>

      {filter === 'open' && (
        <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.5, maxWidth: 760, fontSize: 15 }}>
          Failures that are obviously application errors go to Support on their own and are fixed there. These need
          your decision first — each one says why. Tell Support what should change and how, or close the ones that
          are not bugs, with a reason, so the next run doesn’t raise them again.
        </p>
      )}

      {canTest && selected.length > 0 && (
        <div role="region" aria-label="Selected bugs" style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 16px', borderRadius: 12,
          background: 'var(--color-accent-weak)', border: '1px solid var(--color-accent)',
        }}>
          <strong>{selected.length} bug{selected.length === 1 ? '' : 's'} selected</strong>
          <span style={{ flex: 1 }} />
          {selected.some((d) => d.status === 'open') && (
            <Button onClick={() => onDecide(selected)}>Decide and send to Support…</Button>
          )}
          {selected.some((d) => d.status !== 'closed') && (
            <Button variant="secondary" onClick={() => setClosing(selected.filter((d) => d.status !== 'closed'))}>
              Close as not a bug…
            </Button>
          )}
          {selected.some((d) => d.status === 'closed') && (
            <Button variant="secondary" loading={busy === 'reopen-sel'} onClick={() => void onReopen(selected, 'reopen-sel')}>
              Reopen
            </Button>
          )}
          <Button variant="ghost" onClick={() => setSel(new Set())}>Clear</Button>
        </div>
      )}

      <section aria-label="Bugs" style={{ ...panel, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px', fontSize: 14,
          borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)',
        }}>
          {canTest && sorted.length > 0 && (
            <>
              <input id="tw-bugs-all" type="checkbox" checked={allChecked} style={{ width: 18, height: 18 }}
                     onChange={() => setSel(allChecked ? new Set() : new Set(sorted.map((d) => d.id)))} />
              <label htmlFor="tw-bugs-all">Select all {sorted.length}</label>
            </>
          )}
          <span style={{ flex: 1 }} />
          <span>{loading ? 'Loading…' : 'Sorted by severity'}</span>
        </div>

        {!loading && sorted.length === 0 && (
          <div style={{ padding: '28px 20px', color: 'var(--color-text-secondary)', fontSize: 15 }}>
            {filter === 'open' ? 'Nothing is waiting for a decision.' : 'No bugs here.'}
          </div>
        )}

        {sorted.map((d) => (
          <BugRow key={d.id} project={project} d={d} canTest={canTest} checked={sel.has(d.id)}
                  expanded={expanded.has(d.id)} busy={busy}
                  onCheck={() => setSel((s) => toggle(s, d.id))}
                  onExpand={() => setExpanded((s) => toggle(s, d.id))}
                  onDecide={() => onDecide([d])}
                  onClose={() => setClosing([d])}
                  onReopen={() => void onReopen([d], `reopen-${d.id}`)}
                  onRaise={() => void onRaise(d)}
                  onPatch={(f) => void onPatch(d, f)}
                  onEvidence={() => d.evidence?.run_id && nav.go('runs', { run: d.evidence.run_id, caseId: d.case_id })}
                  onOpenShot={(src, caption) => setLight({ src, caption })} />
        ))}
      </section>

      {closing && (
        <CloseModal
          count={closing.length}
          onClose={() => setClosing(null)}
          onSubmit={async (reason) => {
            try {
              const r = await closeDefects(project, closing.map((d) => d.id), reason)
              toast.success(`Closed ${r.closed.join(', ')}`)
              setSel(new Set())
              setClosing(null)
              await refresh()
            } catch (err) {
              toast.error(apiError(err, 'Could not close the bugs'))
            }
          }}
        />
      )}
      {deciding && (
        <DecideModal
          list={deciding}
          onClose={() => setDeciding(null)}
          onSubmit={async (target, guidance) => {
            try {
              const r = await decideDefects(project, { ids: deciding.map((d) => d.id), decision: 'fix', target, guidance })
              if ('logged' in r) reportLog(r)
              setSel(new Set())
              setDeciding(null)
              await refresh()
            } catch (err) {
              toast.error(apiError(err, 'Could not send the decision to Support'))
            }
          }}
        />
      )}
      {addOpen && (
        <AddBugModal onClose={() => setAddOpen(false)} onSubmit={async (body) => {
          try {
            const d = await createDefect(project, body)
            toast.success(`${d.ref} added`)
            setAddOpen(false)
            await refresh()
          } catch (err) {
            toast.error(apiError(err, 'Could not add the bug'))
          }
        }} />
      )}
      <Lightbox src={light?.src ?? null} caption={light?.caption} onClose={() => setLight(null)} />
    </div>
  )
}

function BugRow({
  project, d, canTest, checked, expanded, busy, onCheck, onExpand, onDecide, onClose, onReopen, onRaise, onPatch,
  onEvidence, onOpenShot,
}: {
  project: string; d: Defect; canTest: boolean; checked: boolean; expanded: boolean; busy: string | null
  onCheck: () => void; onExpand: () => void; onDecide: () => void; onClose: () => void; onReopen: () => void
  onRaise: () => void; onPatch: (f: Partial<Pick<Defect, 'severity' | 'classification'>>) => void
  onEvidence: () => void; onOpenShot: (src: string, caption?: string) => void
}) {
  const ev = d.evidence || {}
  const shots = ev.run_id && d.case_id ? (ev.screenshots || []) : []
  const shot = shots.length ? runFileUrl(project, ev.run_id!, `cases/${d.case_id}/${shots[shots.length - 1]}`) : null
  const [noShot, setNoShot] = useState(false)
  const seen = d.origin === 'manual' ? 'added by a person'
    : d.occurrences > 1 ? `seen in ${d.occurrences} runs` : `found ${fmtTime(d.created_at)}`

  return (
    <article style={{
      display: 'flex', gap: 16, padding: '18px 20px', alignItems: 'flex-start', flexWrap: 'wrap',
      borderTop: '1px solid var(--color-border)', marginTop: -1,
      background: checked ? 'var(--color-accent-weak)' : 'transparent',
    }}>
      {canTest && (
        <input type="checkbox" checked={checked} onChange={onCheck} aria-label={`Select ${d.ref}`}
               style={{ width: 18, height: 18, marginTop: 4 }} />
      )}
      <div style={{
        width: 120, aspectRatio: '16 / 10', borderRadius: 6, overflow: 'hidden', flex: 'none',
        border: '1px solid var(--color-border)', background: 'var(--color-surface-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {shot && !noShot ? (
          <Thumb src={shot} caption={`${d.ref} — ${d.title}`} onOpen={onOpenShot} fluid onMissing={() => setNoShot(true)} />
        ) : <span style={{ ...mutedText, fontSize: 13 }}>No screenshot</span>}
      </div>

      <div style={{ flex: '999 1 380px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Badge tone={SEV_TONE[d.severity] ?? 'neutral'} style={{ textTransform: 'uppercase', letterSpacing: '.05em', fontSize: 13, borderRadius: 6 }}>
            {d.severity}
          </Badge>
          <span style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>{d.ref}</span>
          <span style={{ ...mutedText }}>· {seen}</span>
          {d.status === 'logged' && (
            <Badge tone="info">In Support{d.support_issue != null ? ` #${d.support_issue}` : ''}{d.support_status ? ` · ${d.support_status}` : ''}</Badge>
          )}
          {d.status === 'logged' && d.review?.state === 'auto' && (
            <span title={(d.review.why || []).join('; ')}><Badge tone="neutral">Sent automatically</Badge></span>
          )}
          {d.status === 'logged' && d.review?.decision?.guidance && <Badge tone="neutral">Sent with your guidance</Badge>}
          {d.status === 'closed' && <Badge tone="neutral">Closed{d.closed_reason ? ` · ${d.closed_reason}` : ''}</Badge>}
          {d.retest_passed_run_id && <Badge tone="ok">Passes again since the latest run</Badge>}
        </div>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, lineHeight: 1.35 }}>{d.title}</h2>
        {d.actual && <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.5, fontSize: 15 }}>{d.actual}</p>}
        {d.status === 'open' && (d.review?.reasons?.length ?? 0) > 0 && (
          <div role="note" aria-label={`Why ${d.ref} needs a decision`} style={{
            padding: '10px 12px', borderRadius: 8, fontSize: 14, lineHeight: 1.5,
            background: 'var(--color-warning-weak)', border: '1px solid var(--color-warning)',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Why it needs you</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {d.review!.reasons!.map((r) => <li key={r.code}>{r.text}</li>)}
            </ul>
          </div>
        )}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 14, alignItems: 'center' }}>
          {ev.run_id && d.case_id && (
            <button type="button" onClick={onEvidence} style={{ color: 'var(--color-accent)', padding: 0, fontSize: 14 }}>
              See the evidence
            </button>
          )}
          {d.suspected_object && <span style={mutedText}>Screen: {nice(d.suspected_object, true)}</span>}
          {d.requirement?.length > 0 && <span style={mutedText}>Requirement {d.requirement.join(', ')}</span>}
          <button type="button" onClick={onExpand} aria-expanded={expanded} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-text-secondary)', padding: 0, fontSize: 14,
          }}>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Details
          </button>
        </div>
        {expanded && <BugDetail d={d} canTest={canTest} busy={busy} onRaise={onRaise} onPatch={onPatch} />}
      </div>

      {canTest && (
        <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {d.status === 'open' && (
            <>
              <Button variant="secondary" onClick={onDecide}>Decide…</Button>
              <Button variant="ghost" onClick={onClose}>Not a bug…</Button>
            </>
          )}
          {d.status === 'logged' && <Button variant="ghost" onClick={onClose}>Close…</Button>}
          {d.status === 'closed' && (
            <Button variant="secondary" loading={busy === `reopen-${d.id}`} onClick={onReopen}>Reopen</Button>
          )}
        </div>
      )}
    </article>
  )
}

function BugDetail({ d, canTest, busy, onRaise, onPatch }: {
  d: Defect; canTest: boolean; busy: string | null; onRaise: () => void
  onPatch: (f: Partial<Pick<Defect, 'severity' | 'classification'>>) => void
}) {
  const field = (label: string, v: string | null) => v ? (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>{label}</div>
      <div style={{ fontSize: 15, whiteSpace: 'pre-wrap' }}>{v}</div>
    </div>
  ) : null
  return (
    <div style={{
      marginTop: 6, padding: 14, borderRadius: 8, background: 'var(--color-surface-2)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {field('Should happen', d.expected)}
      {field('Steps to reproduce', d.repro)}
      {field('Why we think so', d.rationale)}
      {d.review?.decision?.guidance && field(
        `Decision${d.review.decision.by_name ? ` by ${d.review.decision.by_name}` : ''} — change ${
          TARGETS.find((t) => t.value === d.review?.decision?.target)?.label.toLowerCase() || 'it'}`,
        d.review.decision.guidance)}
      {d.review?.state === 'auto' && (d.review.why?.length ?? 0) > 0 && field(
        'Sent to Support automatically because', (d.review.why || []).join('; '))}
      {canTest && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', fontSize: 14 }}>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>Severity
            <select value={d.severity} style={inputStyle}
                    onChange={(e) => onPatch({ severity: e.target.value as Severity })}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{nice(s)}</option>)}
            </select>
          </label>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>Cause
            <select value={d.classification} style={inputStyle}
                    onChange={(e) => onPatch({ classification: e.target.value as DefectClassification })}>
              {CLASSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <Button variant="ghost" size="sm" loading={busy === `raise-${d.id}`} onClick={onRaise}>
            Raise a Support issue for it
          </Button>
        </div>
      )}
      <div style={mutedText}>
        First seen {d.first_run_id || '—'} · last {d.last_run_id || '—'}
        {d.logged_at ? ` · sent ${fmtTime(d.logged_at)}` : ''}
        {d.closed_at ? ` · closed ${fmtTime(d.closed_at)}${d.closed_by ? ` by ${d.closed_by}` : ''}` : ''}
      </div>
    </div>
  )
}

function DecideModal({ list, onClose, onSubmit }: {
  list: Defect[]; onClose: () => void; onSubmit: (target: DefectTarget, guidance: string) => Promise<void>
}) {
  const guess: DefectTarget = list.every((d) => d.classification === 'test') ? 'test'
    : list.every((d) => d.classification === 'environment') ? 'data' : 'application'
  const [target, setTarget] = useState<DefectTarget>(guess)
  const [guidance, setGuidance] = useState('')
  const [busy, setBusy] = useState(false)
  const ok = guidance.trim().length >= MIN_GUIDANCE
  const submit = async () => {
    if (!ok) { toast.error('Tell Support how it should be resolved'); return }
    setBusy(true)
    try { await onSubmit(target, guidance.trim()) } finally { setBusy(false) }
  }
  const one = list.length === 1 ? list[0] : null
  return (
    <Modal open onClose={onClose} title={one ? `Decide ${one.ref}` : `Decide ${list.length} bugs`} footer={
      <>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={busy} disabled={!ok} onClick={submit}>Send to Support with this guidance</Button>
      </>
    }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {one && <div style={{ fontSize: 15, fontWeight: 600 }}>{one.title}</div>}
        <div style={{ ...mutedText, fontSize: 14 }}>
          Support’s AI works from what you write here — it diagnoses, plans and builds the fix around it.
          A low-risk fix then goes through on its own; anything riskier comes back to a person in Support.
        </div>
        <div role="radiogroup" aria-label="What should change" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>What should change</div>
          {TARGETS.map((t) => (
            <label key={t.value} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', cursor: 'pointer', borderRadius: 8,
              border: `1px solid ${target === t.value ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
              background: target === t.value ? 'var(--color-accent-weak)' : 'transparent',
            }}>
              <input type="radio" name="tw-decide-target" checked={target === t.value} onChange={() => setTarget(t.value)}
                     style={{ marginTop: 3 }} />
              <span><strong style={{ fontSize: 15 }}>{t.label}</strong>
                <span style={{ ...mutedText, display: 'block', fontSize: 14 }}>{t.help}</span></span>
            </label>
          ))}
        </div>
        <label htmlFor="tw-decide-guidance" style={{ fontSize: 14, fontWeight: 600 }}>How should it be resolved?</label>
        <textarea id="tw-decide-guidance" value={guidance} onChange={(e) => setGuidance(e.target.value)} rows={5}
                  placeholder="e.g. A customer without a GST number is allowed — the save should go through and leave the field empty. Don’t change the validation for registered customers."
                  style={{ ...inputStyle, width: '100%', resize: 'vertical', padding: 10, fontSize: 15 }} />
        {!ok && guidance.length > 0 && (
          <div style={{ ...mutedText, fontSize: 13 }}>A little more, please — at least {MIN_GUIDANCE} characters.</div>
        )}
      </div>
    </Modal>
  )
}

function CloseModal({ count, onClose, onSubmit }: {
  count: number; onClose: () => void; onSubmit: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState(CLOSE_REASONS[0])
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    setBusy(true)
    try { await onSubmit(note.trim() ? `${reason} — ${note.trim()}` : reason) } finally { setBusy(false) }
  }
  return (
    <Modal open onClose={onClose} title={`Close ${count} bug${count === 1 ? '' : 's'}`} footer={
      <>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={busy} disabled={!count} onClick={submit}>Close {count === 1 ? 'bug' : 'bugs'}</Button>
      </>
    }>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...mutedText, fontSize: 14 }}>
          A closed bug stays on record without a Support issue, and the next run doesn’t raise it again.
          You can reopen it, or raise an issue for it later.
        </div>
        <div role="radiogroup" aria-label="Reason" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {CLOSE_REASONS.map((r) => (
            <label key={r} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 40, padding: '0 14px', cursor: 'pointer',
              borderRadius: 'var(--radius-full)', fontSize: 15,
              border: `1px solid ${reason === r ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
              background: reason === r ? 'var(--color-accent-weak)' : 'transparent',
            }}>
              <input type="radio" name="tw-close-reason" checked={reason === r} onChange={() => setReason(r)} /> {r}
            </label>
          ))}
        </div>
        <label htmlFor="tw-close-note" style={{ ...mutedText, fontSize: 14 }}>Note (optional)</label>
        <textarea id="tw-close-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  placeholder="e.g. the application allows free text here on purpose"
                  style={{ ...inputStyle, width: '100%', resize: 'vertical', padding: 10, fontSize: 15 }} />
      </div>
    </Modal>
  )
}

function AddBugModal({ onClose, onSubmit }: {
  onClose: () => void
  onSubmit: (body: { title: string; description: string; severity: Severity; case_id: string | null;
    suspected_object: string | null }) => Promise<void>
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState<Severity>('medium')
  const [caseId, setCaseId] = useState('')
  const [obj, setObj] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    if (!title.trim()) { toast.error('Say what the bug is'); return }
    setBusy(true)
    try {
      await onSubmit({ title: title.trim(), description, severity,
                       case_id: caseId.trim() || null, suspected_object: obj.trim() || null })
    } finally {
      setBusy(false)
    }
  }
  const row = { display: 'flex', flexDirection: 'column' as const, gap: 4, marginBottom: 12, fontSize: 14 }
  return (
    <Modal open onClose={onClose} title="Add a bug you found yourself" footer={
      <>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={busy} disabled={!title.trim()} onClick={submit}>Add bug</Button>
      </>
    }>
      <div style={{ ...mutedText, marginBottom: 12, fontSize: 14 }}>Something you saw that no test caught.</div>
      <label style={row}>What is wrong<input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} /></label>
      <label style={row}>What happened
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
                  style={{ ...inputStyle, resize: 'vertical' }} />
      </label>
      <label style={row}>Severity
        <select value={severity} onChange={(e) => setSeverity(e.target.value as Severity)} style={inputStyle}>
          {SEVERITIES.map((s) => <option key={s} value={s}>{nice(s)}</option>)}
        </select>
      </label>
      <label style={row}>Screen (optional)<input value={obj} onChange={(e) => setObj(e.target.value)} style={inputStyle} /></label>
      <label style={row}>Test case id (optional)<input value={caseId} onChange={(e) => setCaseId(e.target.value)} style={inputStyle} /></label>
    </Modal>
  )
}
